/**
 * La Caminata V8.1 — Células rítmicas (×2 duración) + figuras reales.
 * Reglas de oro:
 * 1) Cambios de cuerda en tiempos fuertes/largos o entre células/compases.
 * 2) Corcheas siempre en pares, mismo pitch que la célula.
 * 3) Consolidación/Integración: figuras entrelazadas (negra / corchea / blanca).
 */
import type {
  AttackFinger,
  RhythmSubMission,
  RhythmTargetNote,
} from "./rhythmPathPlan";
import { alternateIM } from "./rhythmPatternBuilders";

export const att = (
  pitch: string,
  duration: number,
  finger: AttackFinger
): RhythmTargetNote => ({ type: "attack", pitch, duration, finger });

export const rst = (duration: number): RhythmTargetNote => ({
  type: "rest",
  duration,
});

export function repeatNotes(
  cycle: RhythmTargetNote[],
  times: number
): RhythmTargetNote[] {
  const out: RhythmTargetNote[] = [];
  for (let t = 0; t < times; t++) out.push(...cycle);
  return out;
}

export function zipPitches(
  pitches: string[],
  duration: number,
  start: "i" | "m" = "i"
): RhythmTargetNote[] {
  const fingers = alternateIM(pitches.length, start);
  return pitches.map((pitch, i) => att(pitch, duration, fingers[i]));
}

export function cyclePitches(
  cycle: string[],
  count: number,
  duration: number,
  start: "i" | "m" = "i"
): RhythmTargetNote[] {
  const pitches = Array.from({ length: count }, (_, i) => cycle[i % cycle.length]);
  return zipPitches(pitches, duration, start);
}

function block(
  pitch: string,
  count: number,
  duration: number,
  start: "i" | "m" = "i"
): RhythmTargetNote[] {
  return zipPitches(Array(count).fill(pitch) as string[], duration, start);
}

function cell(
  pitch: string,
  durations: number[],
  start: "i" | "m" = "i"
): RhythmTargetNote[] {
  const fingers = alternateIM(durations.length, start);
  return durations.map((d, i) => att(pitch, d, fingers[i]));
}

/** Negra + negra + blanca (pitch del salto en la blanca). */
function cellNegraNegraBlanca(
  shortPitch: string,
  longPitch: string,
  start: "i" | "m" = "i"
): RhythmTargetNote[] {
  const f = alternateIM(3, start);
  return [
    att(shortPitch, 1, f[0]),
    att(shortPitch, 1, f[1]),
    att(longPitch, 2, f[2]),
  ];
}

function lastAttackFinger(notes: RhythmTargetNote[]): "i" | "m" {
  for (let i = notes.length - 1; i >= 0; i--) {
    const f = notes[i].finger;
    if (notes[i].type === "attack" && (f === "i" || f === "m")) return f;
  }
  return "i";
}

function flipIM(f: "i" | "m"): "i" | "m" {
  return f === "i" ? "m" : "i";
}

/** Repite una célula manteniendo alternancia i–m entre repeticiones. */
function repeatCells(
  factory: (start: "i" | "m") => RhythmTargetNote[],
  times: number,
  start: "i" | "m" = "i"
): RhythmTargetNote[] {
  const out: RhythmTargetNote[] = [];
  let s: "i" | "m" = start;
  for (let t = 0; t < times; t++) {
    const chunk = factory(s);
    out.push(...chunk);
    s = flipIM(lastAttackFinger(chunk));
  }
  return out;
}

/** Reasigna i–m continuo a una secuencia (ataques). */
function withFingersFrom(
  notes: RhythmTargetNote[],
  start: "i" | "m"
): RhythmTargetNote[] {
  let f: "i" | "m" = start;
  return notes.map((n) => {
    if (n.type !== "attack") return n;
    const out = { ...n, finger: f as AttackFinger };
    f = flipIM(f);
    return out;
  });
}

const TOL = 150;
const ENDURANCE_RATIO = 0.9;

function step(
  partial: Omit<RhythmSubMission, "targetPattern"> & {
    targetPattern: RhythmTargetNote[];
    instructions: [string, string, string];
    tip: string;
  }
): RhythmSubMission {
  const attacks = partial.targetPattern.filter((n) => n.type === "attack");
  if (attacks.length < 4) {
    throw new Error(
      `[rhythm-01-im] "${partial.id}" tiene ${attacks.length} ataques; mínimo 4.`
    );
  }
  return {
    ...partial,
    attackFingers: attacks.map((n) => n.finger!).filter(Boolean),
    toleranceMs: partial.toleranceMs ?? TOL,
    bpm: partial.bpm ?? 60,
  };
}

/** Mitad del examen (8 compases). Se duplica a 16. */
const EXAM_HALF: RhythmTargetNote[] = [
  ...cellNegraNegraBlanca("E4", "B3", "i"),
  ...cell("E4", [1, 0.5, 0.5, 2], "m"),
  ...cell("B3", [1, 0.5, 0.5, 1, 0.5, 0.5], "m"),
  ...cell("E4", [1, 0.5, 0.5, 1, 0.5, 0.5], "m"),
  ...cell("G3", [0.5, 0.5, 1, 0.5, 0.5, 1], "m"),
  ...cell("E4", [2, 0.5, 0.5, 0.5, 0.5], "m"),
  ...cellNegraNegraBlanca("G3", "B3", "m"),
  ...cell("B3", [0.5, 0.5, 1, 2], "i"),
];

const EXAM_V8: RhythmTargetNote[] = [
  ...EXAM_HALF,
  ...withFingersFrom(EXAM_HALF, flipIM(lastAttackFinger(EXAM_HALF))),
];

export const RHYTHM_01_IM_V5: RhythmSubMission[] = [
  // ── Bloque 1: Redondas ──
  step({
    id: "im-1-1-primer-respiro",
    title: "El primer respiro",
    subtitle: "Redondas → blancas · 1ª",
    blockLabel: "Bloque 1 · Redondas",
    kind: "corto",
    goal: "Ataque largo y luego densificar sin cambiar de cuerda.",
    targetPattern: [...block("E4", 4, 4), ...block("E4", 4, 2)],
    instructions: [
      "Descansa tu pulgar derecho sobre la 6ª cuerda; será tu punto de apoyo.",
      "Cuatro redondas en la 1ª (i–m). Luego cuatro blancas en la misma cuerda.",
      "No cambies de piso: solo cambia la figura.",
    ],
    tip: "La velocidad es un accidente que ocurre cuando tocas lento a la perfección.",
    feedbackOk: "¡Respiro y densificación en la 1ª!",
    feedbackError: "4 redondas + 4 blancas en la 1ª, alternando i–m.",
  }),
  step({
    id: "im-1-2-respiro-invertido",
    title: "Respiro invertido",
    subtitle: "Redondas → blancas · 2ª · m lidera",
    blockLabel: "Bloque 1 · Redondas",
    kind: "corto",
    goal: "Medio lidera; misma idea de densificar.",
    targetPattern: [...block("B3", 4, 4, "m"), ...block("B3", 4, 2, "m")],
    instructions: [
      "Sube a la 2ª cuerda. Empieza con el dedo Medio.",
      "Cuatro redondas (m–i…). Luego cuatro blancas, mismo dedo inicial.",
      "El Medio manda en todo el paso.",
    ],
    tip: "Si el Medio se siente torpe, es normal. Dile que también merece el primer golpe.",
    feedbackOk: "¡El Medio ya lidera densificando!",
    feedbackError: "4 redondas + 4 blancas en la 2ª, empezando con m.",
  }),
  step({
    id: "im-1-3-primer-cruce",
    title: "El primer cruce",
    subtitle: "Pares y alterno · redondas",
    blockLabel: "Bloque 1 · Redondas",
    kind: "medio",
    goal: "Cruzar 1ª↔2ª con dos agrupaciones distintas.",
    targetPattern: [
      ...zipPitches(["E4", "E4", "B3", "B3"], 4),
      ...zipPitches(["E4", "B3", "E4", "B3"], 4),
    ],
    instructions: [
      "Primero en pares: dos redondas en 1ª, dos en 2ª.",
      "Luego alterno: 1ª–2ª–1ª–2ª.",
      "Nunca repitas el mismo dedo al cambiar de cuerda.",
    ],
    tip: "Cambiar la agrupación obliga a replanear el cruce. Eso es el ejercicio.",
    feedbackOk: "¡Cruce con dos agrupaciones!",
    feedbackError: "Pares 1ª×2–2ª×2, luego alterno. Redondas.",
  }),
  step({
    id: "im-1-4-resistencia-i",
    title: "Resistencia I",
    subtitle: "Forma A–A–B–A · redondas",
    blockLabel: "Bloque 1 · Redondas",
    kind: "resistencia",
    isEndurance: true,
    goal: "Paciencia con forma musical (no un péndulo infinito).",
    targetPattern: [
      // A
      ...zipPitches(["E4", "E4", "B3", "B3"], 4),
      // A
      ...zipPitches(["E4", "E4", "B3", "B3"], 4),
      // B
      ...zipPitches(["E4", "B3", "E4", "B3"], 4),
      // A
      ...zipPitches(["E4", "E4", "B3", "B3"], 4),
      // Coda: bloques
      ...block("E4", 4, 4),
      ...block("B3", 4, 4),
    ],
    successRatio: ENDURANCE_RATIO,
    instructions: [
      "Forma A–A–B–A: A = dos en 1ª y dos en 2ª; B = alterno nota a nota.",
      "Coda: cuatro redondas solo en 1ª y cuatro solo en 2ª.",
      "Si fallas un golpe, retoma en el siguiente sin pelearte con la mano.",
    ],
    tip: "Si te aburre, estás anticipando. Escucha el cambio de frase.",
    feedbackOk: "Forma AABA sostenida. ¡Bloque 1 listo!",
    feedbackError: "Respeta A–A–B–A y la coda. Redondas.",
  }),

  // ── Bloque 2: Blancas ──
  step({
    id: "im-1-5-despertando",
    title: "Despertando",
    subtitle: "Blancas → negras · 3ª",
    blockLabel: "Bloque 2 · Blancas",
    kind: "corto",
    goal: "Misma cuerda, dos figuras.",
    targetPattern: [...block("G3", 4, 2), ...block("G3", 8, 1)],
    instructions: [
      "Bienvenido a la 3ª cuerda.",
      "Cuatro blancas para asentar; luego ocho negras en la misma cuerda.",
      "Sin cambiar de piso: solo densificas.",
    ],
    tip: "Siente el cambio de figura sin apretar.",
    feedbackOk: "¡La 3ª despierta con forma!",
    feedbackError: "4 blancas + 8 negras solo en la 3ª.",
  }),
  step({
    id: "im-1-6-salto-seguro",
    title: "Salto seguro",
    subtitle: "1ª → 3ª → vuelta · blancas",
    blockLabel: "Bloque 2 · Blancas",
    kind: "corto",
    goal: "Ida y vuelta con anclaje.",
    targetPattern: [
      ...block("E4", 4, 2),
      ...block("G3", 4, 2),
      ...block("E4", 4, 2),
    ],
    instructions: [
      "Cuatro blancas en la 1ª, cuatro en la 3ª, y vuelves a cuatro en la 1ª.",
      "El salto ocurre solo en los cambios de bloque.",
      "Empieza con Índice.",
    ],
    tip: "La vuelta a la 1ª confirma que el mapa quedó grabado.",
    feedbackOk: "¡Ida y vuelta segura!",
    feedbackError: "Bloques de 4 blancas: 1ª, 3ª, 1ª.",
  }),
  step({
    id: "im-1-7-sumando-pasado",
    title: "Sumando el pasado",
    subtitle: "Blancas + redonda · dos direcciones",
    blockLabel: "Bloque 2 · Blancas",
    kind: "medio",
    goal: "Aterrizar en figura larga; ida y espejo.",
    targetPattern: [
      ...repeatCells(
        (s) => [
          att("E4", 2, s),
          att("E4", 2, flipIM(s)),
          att("B3", 4, s),
        ],
        2,
        "i"
      ),
      ...repeatCells(
        (s) => [
          att("B3", 2, s),
          att("B3", 2, flipIM(s)),
          att("E4", 4, s),
        ],
        2,
        "i"
      ),
    ],
    instructions: [
      "Primero: dos blancas en 1ª y redonda en 2ª (×2).",
      "Luego el espejo: dos blancas en 2ª y redonda en 1ª (×2).",
      "¡Nunca repitas el mismo dedo al cambiar de cuerda!",
    ],
    tip: "El espejo obliga a replantear el salto. No es el mismo loop.",
    feedbackOk: "¡Mixto con espejo!",
    feedbackError: "Ida 1ª→2ª y espejo 2ª→1ª. Blancas + redonda.",
  }),
  step({
    id: "im-1-8-resistencia-ii",
    title: "Resistencia II",
    subtitle: "Recorrido · blancas",
    blockLabel: "Bloque 2 · Blancas",
    kind: "resistencia",
    isEndurance: true,
    goal: "Tres cuerdas con recorrido de ida, vuelta y ancla.",
    targetPattern: [
      ...block("E4", 4, 2),
      ...block("B3", 4, 2),
      ...block("G3", 4, 2),
      ...block("B3", 4, 2),
      ...block("E4", 4, 2),
      ...block("G3", 4, 2),
      ...block("E4", 4, 2),
      ...block("B3", 4, 2),
    ],
    successRatio: ENDURANCE_RATIO,
    instructions: [
      "Recorre: 1ª → 2ª → 3ª → 2ª → 1ª.",
      "Luego contraste: 3ª → 1ª → 2ª (bloques de cuatro blancas).",
      "El cambio de cuerda llega solo al terminar cada bloque.",
    ],
    tip: "Un recorrido tiene dirección. No es un carrusel infinito.",
    feedbackOk: "Recorrido de tres cuerdas. Constancia de verdad.",
    feedbackError: "Sigue el mapa de bloques. Blancas.",
  }),

  // ── Bloque 3: Negras ──
  step({
    id: "im-1-9-primer-latido",
    title: "El primer latido",
    subtitle: "Negras · respiro · negras · 2ª",
    blockLabel: "Bloque 3 · Negras",
    kind: "corto",
    goal: "Latido con respiro en el medio.",
    targetPattern: [
      ...block("B3", 8, 1),
      ...block("B3", 2, 2),
      ...block("B3", 8, 1),
    ],
    instructions: [
      "Ocho negras en la 2ª.",
      "Respiro: dos blancas en la misma cuerda.",
      "Otras ocho negras. El respiro corta el trance.",
    ],
    tip: "Usa el respiro para soltar hombros. Luego retoma el latido.",
    feedbackOk: "¡Latido con forma!",
    feedbackError: "8 negras, 2 blancas, 8 negras — todo en la 2ª.",
  }),
  step({
    id: "im-1-10-escalera-guiada",
    title: "Escalera guiada",
    subtitle: "3ª → 2ª → 3ª · negras",
    blockLabel: "Bloque 3 · Negras",
    kind: "corto",
    goal: "Bajar y volver con bloques.",
    targetPattern: [
      ...block("G3", 8, 1),
      ...block("B3", 8, 1),
      ...block("G3", 8, 1),
    ],
    instructions: [
      "Ocho negras en la 3ª, ocho en la 2ª, y vuelves a ocho en la 3ª.",
      "El cambio ocurre al inicio de cada bloque.",
      "Un click, un golpe.",
    ],
    tip: "La vuelta a la 3ª confirma el mapa. No es solo bajar.",
    feedbackOk: "¡Escalera de ida y vuelta!",
    feedbackError: "Bloques de 8 negras: 3ª, 2ª, 3ª.",
  }),
  step({
    id: "im-1-11-salto-ritmico",
    title: "Salto rítmico",
    subtitle: "Negra, negra, blanca · 1ª → 2ª · ×4",
    blockLabel: "Bloque 3 · Negras",
    kind: "medio",
    goal: "Célula mixta simple. El salto de cuerda ocurre en la blanca.",
    targetPattern: repeatCells(
      (s) => cellNegraNegraBlanca("E4", "B3", s),
      4,
      "i"
    ),
    instructions: [
      "La célula es: negra, negra, blanca.",
      "Las dos negras van en la 1ª; la blanca salta a la 2ª.",
      "Cuatro repeticiones. El salto vive en la figura larga.",
    ],
    tip: "Aterrizar en blanca es el paracaídas del salto. Úsalo.",
    feedbackOk: "¡Célula negra–negra–blanca!",
    feedbackError: "Negra, negra en 1ª y blanca en 2ª. Repite.",
  }),
  step({
    id: "im-1-12-resistencia-iii",
    title: "Resistencia III",
    subtitle: "Recorrido · negras",
    blockLabel: "Bloque 3 · Negras",
    kind: "resistencia",
    isEndurance: true,
    goal: "Resistencia con mapa, no 64 clones.",
    targetPattern: [
      ...block("E4", 8, 1),
      ...block("B3", 8, 1),
      ...block("G3", 8, 1),
      ...block("B3", 8, 1),
      ...block("E4", 8, 1),
      // Contraste: pares
      ...zipPitches(
        ["E4", "E4", "B3", "B3", "G3", "G3", "B3", "B3"],
        1
      ),
      ...zipPitches(
        ["E4", "E4", "B3", "B3", "G3", "G3", "B3", "B3"],
        1
      ),
      ...block("E4", 8, 1),
    ],
    successRatio: ENDURANCE_RATIO,
    instructions: [
      "Primero un recorrido: 1ª → 2ª → 3ª → 2ª → 1ª (ocho negras por piso).",
      "Luego pares: dos en cada cuerda ciclando el mapa.",
      "Cierra con ocho negras en la 1ª.",
    ],
    tip: "Cada tramo pide otra intención. Relaja la muñeca.",
    feedbackOk: "Recorrido en negras. El latido ya es tuyo.",
    feedbackError: "Sigue recorrido, pares y cierre. Negras.",
  }),

  // ── Bloque 4: Corcheas ──
  step({
    id: "im-1-13-primer-par",
    title: "El primer par",
    subtitle: "2 corcheas + negra + blanca · 1ª · ×2",
    blockLabel: "Bloque 4 · Corcheas protegidas",
    kind: "corto",
    goal: "Par de corcheas y freno largo para asimilar.",
    targetPattern: repeatCells(
      (s) => cell("E4", [0.5, 0.5, 1, 2], s),
      2,
      "i"
    ),
    instructions: [
      "Tocaremos dos corcheas en un solo click del metrónomo.",
      "Todo en la 1ª cuerda, seguidas de una negra y una blanca.",
      "Ataca el par con i–m y relaja de inmediato.",
    ],
    tip: "El par de corcheas es breve. La negra y la blanca existen para asimilar.",
    feedbackOk: "¡Primer par de corcheas controlado!",
    feedbackError: "2 corcheas + negra + blanca, todo en la 1ª. ×2.",
  }),
  step({
    id: "im-1-14-trote-repetido",
    title: "Pares repetidos",
    subtitle: "2 corcheas + negra + silencio · 2ª · ×4",
    blockLabel: "Bloque 4 · Corcheas protegidas",
    kind: "corto",
    goal: "Agrupar corcheas de a dos. Sin cambio de cuerda.",
    targetPattern: repeatCells(
      (s) => [
        ...cell("B3", [0.5, 0.5, 1], s),
        rst(1),
      ],
      4,
      "i"
    ),
    instructions: [
      "Par de corcheas (i–m) en la 2ª cuerda.",
      "Después: una negra y un silencio. El silencio también cuenta.",
      "Cuatro ciclos. Sin cambiar de cuerda.",
    ],
    tip: "Agrupar de a dos es más seguro que acelerar nota a nota.",
    feedbackOk: "¡Pares anclados en la 2ª!",
    feedbackError: "2 corcheas + negra + silencio ×4, solo en la 2ª. No ataques en el shh.",
  }),
  step({
    id: "im-1-15-combinacion-agil",
    title: "Combinación ágil",
    subtitle: "Negra + 2 corcheas + blanca · 1ª · ×4",
    blockLabel: "Bloque 4 · Corcheas protegidas",
    kind: "medio",
    goal: "Mezcla real en 1 compás. Todo en la 1ª cuerda.",
    targetPattern: repeatCells(
      (s) => cell("E4", [1, 0.5, 0.5, 2], s),
      4,
      "i"
    ),
    instructions: [
      "Un compás mixto: negra, dos corcheas y blanca.",
      "Todo en la 1ª cuerda. Sin saltos espaciales: solo mezcla de figuras.",
      "Cuatro repeticiones. Si llegas temprano a la blanca, estás acelerando de más.",
    ],
    tip: "Negra = un click. Dos corcheas = un click partido. Blanca = dos clicks.",
    feedbackOk: "¡Célula negra–corcheas–blanca!",
    feedbackError: "Negra + 2 corcheas + blanca, solo en la 1ª. ×4.",
  }),
  step({
    id: "im-1-16-resistencia-iv",
    title: "Resistencia IV",
    subtitle: "Corcheas · freno · corcheas",
    blockLabel: "Bloque 4 · Corcheas protegidas",
    kind: "resistencia",
    isEndurance: true,
    goal: "Velocidad con respiros; un solo cambio de cuerda a mitad.",
    targetPattern: [
      ...block("E4", 16, 0.5),
      ...block("E4", 4, 1),
      ...block("E4", 16, 0.5),
      ...block("B3", 16, 0.5),
      ...block("B3", 4, 1),
      ...block("B3", 16, 0.5),
    ],
    successRatio: ENDURANCE_RATIO,
    instructions: [
      "En la 1ª: 16 corcheas, 4 negras (freno), 16 corcheas.",
      "Salta a la 2ª y repite la misma forma.",
      "El único cambio de cuerda está a mitad. Los frenos cortan el trance.",
    ],
    tip: "¡No aprietes la mandíbula! El freno a negras es parte del drill.",
    feedbackOk: "Inercia con forma. Un solo cruce a la mitad.",
    feedbackError: "Corcheas–negras–corcheas en 1ª y luego en 2ª.",
  }),

  // ── Bloque 5: Integración ──
  step({
    id: "im-1-17-el-clasico",
    title: "El clásico",
    subtitle: "Negra + 2 corcheas · ×2 por cuerda",
    blockLabel: "Bloque 5 · Integración",
    kind: "medio",
    goal: "Célula negra + 2 corcheas iterada. Dos compases en 2ª, dos en 1ª.",
    targetPattern: (() => {
      const onB = repeatCells(
        (s) => cell("B3", [1, 0.5, 0.5, 1, 0.5, 0.5], s),
        2,
        "i"
      );
      const onE = repeatCells(
        (s) => cell("E4", [1, 0.5, 0.5, 1, 0.5, 0.5], s),
        2,
        flipIM(lastAttackFinger(onB))
      );
      return [...onB, ...onE];
    })(),
    instructions: [
      "Ritmo mixto: una negra seguida de dos corcheas, y otra vez.",
      "La secuencia por compás: negra, 2 corcheas, negra, 2 corcheas.",
      "Dos compases en la 2ª cuerda, luego dos en la 1ª. ¡No repitas dedos al cambiar!",
    ],
    tip: "El salto de cuerda llega solo entre bloques de compases, nunca partiendo un par de corcheas.",
    feedbackOk: "¡El clásico clavado!",
    feedbackError: "Negra + 2 corcheas repetido. Primero 2ª (×2), luego 1ª (×2).",
  }),
  step({
    id: "im-1-18-reflejo-invertido",
    title: "Reflejo invertido",
    subtitle: "2 corcheas + negra · 1ª · ×4",
    blockLabel: "Bloque 5 · Integración",
    kind: "medio",
    goal: "Célula 2 corcheas + negra. Frenar tras acelerar.",
    targetPattern: repeatCells(
      (s) => cell("E4", [0.5, 0.5, 1, 0.5, 0.5, 1], s),
      4,
      "i"
    ),
    instructions: [
      "Invertimos el orden: entramos con dos corcheas y frenamos en negra.",
      "Por compás: 2 corcheas, negra, 2 corcheas, negra.",
      "Concéntrate en el freno: tras las corcheas, la mano se relaja para la negra a tiempo.",
    ],
    tip: "Si te adelantas a la negra, el reflejo falló. La negra es el ancla.",
    feedbackOk: "¡Reflejo invertido bajo control!",
    feedbackError: "2 corcheas + negra, repetido ×4. Todo en la 1ª.",
  }),
  step({
    id: "im-1-19-sincopa-segura",
    title: "La síncopa segura",
    subtitle: "Blanca + 4 corcheas · ×4",
    blockLabel: "Bloque 5 · Integración",
    kind: "medio",
    goal: "Contraste: blanca y luego 4 corcheas sin perder i–m.",
    targetPattern: (() => {
      const onE = repeatCells(
        (s) => cell("E4", [2, 0.5, 0.5, 0.5, 0.5], s),
        2,
        "i"
      );
      const onB = repeatCells(
        (s) => cell("B3", [2, 0.5, 0.5, 0.5, 0.5], s),
        2,
        flipIM(lastAttackFinger(onE))
      );
      return [...onE, ...onB];
    })(),
    instructions: [
      "Primero una blanca. Luego cuatro corcheas seguidas.",
      "Dos células en la 1ª, luego dos en la 2ª. El salto ocurre al cambiar de bloque.",
      "Mantén i–m limpio en las corcheas. No aprietes la mandíbula.",
    ],
    tip: "El contraste enseña control: deja sonar la blanca, luego el grupo de corcheas anclado.",
    feedbackOk: "¡Síncopa segura!",
    feedbackError: "Blanca + 4 corcheas. Dos veces en 1ª y dos en 2ª.",
  }),
  step({
    id: "im-1-20-examen-final",
    title: "Examen Final: La Caminata",
    subtitle: "16 compases · células · 3 cuerdas",
    blockLabel: "Bloque 5 · Integración",
    kind: "examen",
    isEndurance: true,
    goal: "Mezcla musical de todas las células, navegando las 3 cuerdas.",
    targetPattern: EXAM_V8,
    successRatio: ENDURANCE_RATIO,
    instructions: [
      "Dieciséis compases con las células que ya conoces: negras, corcheas y blancas mezcladas.",
      "Cuando haya corcheas, te quedas en una sola cuerda dentro de la célula.",
      "Tu único jefe es el metrónomo: no pares hasta el último golpe.",
    ],
    tip: "Esto no es un castigo: es el gimnasio de figuras reales — con redes de seguridad.",
    feedbackOk: "Gimnasio superado. i–m ya mezclan figuras con control.",
    feedbackError: "Examen de 16 compases. El metrónomo manda hasta el final.",
  }),
];
